import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';
import nl from './locales/nl.json';
import it from './locales/it.json';
import pt from './locales/pt.json';
import ru from './locales/ru.json';
import zh from './locales/zh.json';

export const SUPPORTED_LANGUAGES = [
  'en',
  'es',
  'fr',
  'de',
  'nl',
  'it',
  'pt',
  'ru',
  'zh'
] as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    // Browsers report region-tagged codes ("de-DE", "zh-CN", "pt-BR"). Without
    // these two options i18n.language stayed "de-DE": the copy rendered in
    // German (the resolve hierarchy still found "de") but every `=== 'de'`
    // comparison in the UI failed, so the language selector claimed the user
    // was on English. Strip the region on both the detection and the
    // resolution side so the whole app sees a plain "de".
    load: 'languageOnly',
    nonExplicitSupportedLngs: true,
    resources: {
      en: { translation: en },
      es: { translation: es },
      fr: { translation: fr },
      de: { translation: de },
      nl: { translation: nl },
      it: { translation: it },
      pt: { translation: pt },
      ru: { translation: ru },
      zh: { translation: zh }
    },
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'squarebit_store_lang',
      caches: ['localStorage'],
      convertDetectedLanguage: (lng: string) => lng.split('-')[0]
    }
  });

// Keep <html lang> in step with the active language. It was hardcoded to "en"
// in index.html and never updated, which gave Chinese and Russian pages the
// wrong font/line-breaking rules, broke automatic hyphenation of long German
// compounds, and reported the wrong language to screen readers and crawlers.
const applyDocumentLanguage = (lng?: string) => {
  const base = (lng || 'en').split('-')[0];
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', base);
  }
};

applyDocumentLanguage(i18n.resolvedLanguage || i18n.language);
i18n.on('languageChanged', applyDocumentLanguage);

export default i18n;
